/**
 * Three.js scene setup and the per-frame draw.
 *
 * Everything engine-specific lives here, in voxelMaterial.ts and in atlas.ts.
 * Meshing itself is core/ code running on worker threads.
 */

import * as THREE from "three";
import { World } from "../core/world/world.js";
import { CHUNK_SIZE } from "../core/world/chunk.js";
import type { PlayerController } from "../core/player/controller.js";
import type { DayNightCycle } from "../core/world/daynight.js";
import { skinById, type SkinDef } from "../core/player/skins.js";
import { blockDef, waterFlowId, BlockId } from "../core/block/registry.js";
import {
  paintFlame, FLAME_W, FLAME_H,
} from "../core/block/flameSprite.js";
import {
  paintCracks, CRACK_SIZE, CRACK_STAGES,
} from "../core/block/crackSprite.js";
import type { Villager } from "../core/entity/villager.js";
import { arrowAim, type Arrow } from "../core/entity/arrow.js";
import type { Firebolt } from "../core/entity/firebolt.js";
import type { Orc } from "../core/entity/orc.js";
import { type Pickup } from "../core/entity/pickup.js";
import { itemTile } from "../core/block/items.js";
import type { SolidTest } from "../core/physics/voxelCollision.js";
import { easeStep } from "../core/player/stepEase.js";
import type { Dragon } from "../core/entity/dragon.js";
import { raycastVoxels } from "../core/physics/raycast.js";
import { PlayerModel, ViewArm } from "./playerModel.js";
import type { Hand } from "../core/player/hand.js";
import { strikeProgress, STRIKE_SECONDS } from "../core/player/strike.js";
import { makeHeldMesh, disposeHeldMesh } from "./heldItem.js";
import { pickupGeometry, PICKUP_PICTURE } from "./pickupShape.js";
import { preloadHeldModels, heldModel } from "./heldModels.js";
import { preloadSkinModels } from "./skinModels.js";
import { BlockModels } from "./blockModels.js";
import { PlainItemId } from "../core/block/items.js";
import type { Tone } from "../core/text/tone.js";
import { BeastModel } from "./beastModel.js";
import { FishModel, tintKaras } from "./fishModel.js";
import { BoatModel, tintBoats } from "./boatModel.js";
import { TankModel, tintTanks } from "./tankModel.js";
import type { Tank } from "../core/entity/tank.js";
import { worthDrawing } from "../core/entity/visibility.js";
import type { Fish } from "../core/entity/fish.js";
import type { Boat } from "../core/entity/boat.js";
import type { Beast } from "../core/entity/beast.js";
import { buildBlockTextureArray } from "./atlas.js";
import { createVoxelMaterials, type VoxelMaterialUniforms } from "./voxelMaterial.js";
import { MeshWorkerPool } from "./meshWorkerPool.js";
import type { MeshResponse } from "./meshWorker.js";
import { Sky } from "./sky.js";

/** Distance at which terrain starts fading into the sky, in blocks. */
const FOG_NEAR = 60;
const FOG_FAR = 190;

/** How far behind the player the third-person camera sits, in blocks. */
const THIRD_PERSON_DISTANCE = 3.6;
/** Never let the camera get closer than this, even wedged against a wall. */
const MIN_CAMERA_DISTANCE = 0.7;
/** Murky green-blue, and close in, when the camera is under the surface. */
/**
 * How far you can see under water, and in what colour.
 *
 * Sixteen blocks, which is roughly what a block game gives you: the point of
 * being under water is that you cannot see, and a lake you can see across is a
 * lake with no bottom to find. The colour is darker and greener than the water
 * itself so that distance reads as depth rather than as a blue box.
 */
const UNDERWATER_FOG = 0x123a4f;
const UNDERWATER_NEAR = 1;
const UNDERWATER_FAR = 16;
const UNDERWATER_TINT = new THREE.Color(0.55, 0.78, 0.95);

/** Stride length: how much swing phase one block of travel is worth. */
const STRIDE = 2.2;

/**
 * How many chunks may be handed to workers in one frame.
 *
 * Copying a padded volume is main-thread work, so queueing all 194 chunks at
 * startup in a single frame would reintroduce exactly the freeze the workers
 * are there to remove. A handful per frame keeps the pool fed and the frame
 * short; the world fills in over about half a second instead.
 */
const MAX_SUBMITS_PER_FRAME = 8;

export type CameraMode = "first" | "third" | "thirdFront";

/** Order the C key cycles through. */
export const CAMERA_MODES: readonly CameraMode[] = ["first", "third", "thirdFront"];

export interface RenderStats {
  chunks: number;
  triangles: number;
  /** Chunks currently being meshed on a worker. */
  meshing: number;
}

/**
 * How wide the view is, walking and running.
 *
 * Five degrees. Enough that the edges of the world slide outwards as you
 * break into a run and the ground reads as passing faster; small enough that
 * nobody notices it as an effect, which is the point. Speed you can only
 * measure is speed nobody feels - running was a third faster than walking for
 * a year and went unnoticed.
 */
export const WALK_FOV = 75;
export const RUN_FOV = 80;
/** How fast the view settles, per second. Quick enough to feel like a stride. */
const FOV_EASE = 9;

/** Anything the camera can sit in: a body, or a machine with a lens. */
export interface CameraTarget {
  eyePosition(): { x: number; y: number; z: number };
  readonly yaw: number;
  readonly pitch: number;
  /**
   * Whether its feet are down, which is what makes a rise a *step*.
   *
   * Optional because a дрон has no feet and nothing to smooth: it flies, and
   * a flying thing that eased its way up would feel like a flying thing on a
   * rope.
   */
  readonly onGround?: boolean;
}

export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  /** How far the camera is still behind the eye after a step. See easeStep. */
  private readonly dragonModels = new Map<number, BeastModel>();
  private stepLag = 0;
  private lastEyeY = Number.NaN;

  readonly sky: Sky;

  /** Wireframe box drawn around the block currently aimed at. */
  private readonly highlight: THREE.LineSegments;

  /**
   * And the cracks, over the block being worked at.
   *
   * A separate box from the outline because it comes and goes on a different
   * question: the outline says "this is what you would hit", the cracks say
   * "this is how far in you are".
   */
  private readonly cracking: THREE.Mesh;

  private crackMaps: THREE.Texture[] | null = null;

  /** The player's own body. Only drawn in third person. */
  private playerModel: PlayerModel | null = null;

  /**
   * What you hold, drawn in front of the camera in first person.
   *
   * A child of the camera rather than something placed in the world each
   * frame: it then cannot lag a frame behind where you are looking, which is
   * the one thing that makes a view model feel wrong rather than merely look
   * wrong.
   */
  private readonly viewHand = new THREE.Group();
  private viewArm: ViewArm | null = null;
  private viewId = 0;
  private viewTone: Tone = "family";
  private viewShield = 0;
  private hand: Hand = "right";

  /**
   * Bodies for the villagers, keyed by entity id.
   *
   * A map rather than an array because villagers come and go - turning the
   * 18+ switch off has to take дядько Гриць with it - and rebuilding every
   * model when one of them leaves would rebuild every texture too.
   */
  private readonly villagerModels = new Map<number, PlayerModel>();

  /**
   * Bodies for the orcs.
   *
   * One model each, as for the villagers. Every orc wears the same skin, so a
   * raid of forty uploads the same texture forty times - worth sharing when
   * there is a raid to measure, and not worth guessing at before.
   */
  private readonly orcModels = new Map<number, PlayerModel>();
  private readonly beastModels = new Map<number, BeastModel>();
  private readonly fishModels = new Map<number, FishModel>();
  private readonly boatModels = new Map<number, BoatModel>();
  private readonly tankModels = new Map<number, TankModel>();

  /**
   * Arrow meshes, keyed by entity id.
   *
   * All of them share one geometry and one material: an arrow is four boxes'
   * worth of triangles and there may be a dozen in the air, so giving each its
   * own would be a lot of allocation for a stick.
   */
  private readonly arrowMeshes = new Map<number, THREE.Mesh>();
  private readonly otherModels = new Map<number, PlayerModel>();
  /** Somebody else's drone, while it is up. */
  private readonly otherDrones = new Map<number, THREE.Object3D>();
  /** And your own, on the frames when you are not the one inside it. */
  private ownDrone: THREE.Object3D | null = null;
  /** Where each of them was last snapshot, for working out a stride. */
  private readonly otherWhere = new Map<number, { x: number; z: number }>();
  private arrowGeometry: THREE.BufferGeometry | null = null;
  private arrowMaterial: THREE.Material | null = null;
  private boltGeometry: THREE.BufferGeometry | null = null;
  private boltMaterial: THREE.Material | null = null;
  private coldMaterial: THREE.Material | null = null;
  private readonly boltMeshes = new Map<number, THREE.Mesh>();
  private readonly flameMeshes = new Map<string, THREE.Group>();
  /** Flames on things that walk, which is a different map because they move. */
  private readonly bodyFlames = new Map<string, THREE.Group>();

  /**
   * Meshes for things on the ground, and one small cube geometry per item
   * texture.
   *
   * Cached by tile rather than by entity: a mined seam drops fifty lumps of
   * coal, and fifty identical geometries is fifty times the upload for one
   * cube.
   */
  private readonly pickupMeshes = new Map<number, THREE.Object3D>();
  /** Advances with distance travelled, so the stride matches the feet. */
  private swingPhase = 0;
  /**
   * How many blows each body has struck, and how long is left of the current
   * one. See syncPlayers: the count comes over the wire, the clock is ours.
   */
  private readonly otherBlows = new Map<number, number>();
  private readonly otherSwing = new Map<number, number>();
  /** The same two for the orcs: blows last seen, and what is left of the swing. */
  private readonly orcBlows = new Map<number, number>();
  private readonly orcSwing = new Map<number, number>();
  /** Each body's own stride phase, advanced by how far it has walked. */
  private readonly otherPhase = new Map<number, number>();

  cameraMode: CameraMode = "first";
  /**
   * Draw the player's body even in first person.
   *
   * True while the camera is in the drone: you are not behind those eyes, so
   * the rule that hides your own body does not apply - and looking down to
   * see yourself standing in a field is the whole point of the trade.
   */
  showOwnBody = false;

  /** [opaque, blended] - the order geometry groups index into. */
  private readonly materials: [THREE.ShaderMaterial, THREE.ShaderMaterial];
  private readonly uniforms: VoxelMaterialUniforms;
  private readonly chunkMeshes = new Map<string, THREE.Mesh>();
  private readonly forward = new THREE.Vector3();
  /** Scratch vector for project(). Reused so a per-frame call allocates nothing. */
  private readonly projected = new THREE.Vector3();

  /** Meshing runs off the main thread; results arrive whenever they arrive. */
  private readonly meshPool: MeshWorkerPool;
  /** Chunk version each mesh was built from, so stale work can be spotted. */
  private readonly meshedVersion = new Map<string, number>();
  private triangleCount = 0;

  stats: RenderStats = { chunks: 0, triangles: 0, meshing: 0 };

  constructor(canvasParent: HTMLElement) {
    // The modelled things, brought in up front; every hand draws its
    // drawing until they arrive and its model after. See heldModels.
    void preloadHeldModels();
    void preloadSkinModels();
    this.renderer = new THREE.WebGLRenderer({
      antialias: false, // pixel-art look; also cheaper on integrated GPUs
      powerPreference: "high-performance",
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    // Cap at 2x - a 2019 MBP Retina panel at full DPR costs a lot for no gain.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    canvasParent.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    // Colour is driven by the day/night cycle in updateEnvironment().
    this.scene.fog = new THREE.Fog(0x000000, FOG_NEAR, FOG_FAR);

    this.camera = new THREE.PerspectiveCamera(
      WALK_FOV,
      window.innerWidth / window.innerHeight,
      0.1,
      500,
    );

    /*
     * The camera goes into the scene.
     *
     * Only so that what hangs off it is drawn - three.js renders the graph
     * under the scene, and a camera that is not in it has children nobody
     * visits. The camera itself draws nothing.
     */
    this.scene.add(this.camera);
    this.camera.add(this.viewHand);

    const built = createVoxelMaterials(
      buildBlockTextureArray(), FOG_NEAR, FOG_FAR,
      // Which atlas layer the shader should scroll. Taken from the registry
      // rather than typed in twice: a tile renumbered on one side and not the
      // other would animate the wrong block, or nothing at all.
      blockDef(waterFlowId(1)).faces.side,
      // And which one keeps its own brightness after dark. Same reasoning:
      // read from the registry, never typed in twice.
      blockDef(BlockId.Torch).faces.side,
      // And the one with lightning running over it.
      blockDef(BlockId.Random).faces.side,
    );
    this.materials = [built.opaque, built.blended];
    this.uniforms = built.uniforms;
    this.meshPool = new MeshWorkerPool((result) => this.applyMesh(result));

    this.sky = new Sky();
    this.blockModels = new BlockModels(this.scene);
    this.scene.add(this.sky.group);

    // Slightly larger than a block so the outline doesn't z-fight with the
    // face it sits on.
    this.highlight = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1.004, 1.004, 1.004)),
      new THREE.LineBasicMaterial({ color: 0x0d0d0d, transparent: true, opacity: 0.65 }),
    );
    this.highlight.visible = false;
    this.scene.add(this.highlight);

    /*
     * A hair larger than the block, and never written to the depth buffer.
     *
     * The cracks are a picture laid over six faces that are already there.
     * Drawn at exactly one they z-fight; drawn opaque they would hide the
     * block they are cracking. `alphaTest` keeps the pixels between the
     * fissures from being drawn at all, which is what makes this a stencil
     * over the block rather than a cage round it.
     */
    this.cracking = new THREE.Mesh(
      new THREE.BoxGeometry(1.008, 1.008, 1.008),
      new THREE.MeshBasicMaterial({
        transparent: true, alphaTest: 0.4, depthWrite: false,
        toneMapped: false,
      }));
    this.cracking.visible = false;
    this.scene.add(this.cracking);

    window.addEventListener("resize", this.onResize);
  }

  /** Should this thing have a body right now? See core/entity/visibility.ts. */
  private nearEnough(x: number, y: number, z: number, hasModel: boolean): boolean {
    const c = this.camera.position;
    return worthDrawing(x, y, z, c.x, c.y, c.z, hasModel);
  }

  /**
   * Put something in the player's hands, in both views.
   *
   * The body's hand and the fist in front of the camera are the same thing
   * seen two ways, and they are set together so they cannot disagree - which
   * they would, silently, the first time somebody updated one call site.
   */
  /**
   * Put something in the player's hands, in both views.
   *
   * The body's hand and your own hand are the same thing seen two ways, and
   * they are set together so they cannot disagree - which they would,
   * silently, the first time somebody updated one call site.
   */
  setHeld(id: number, tone: Tone): void {
    this.playerModel?.setHeld(id, tone);
    this.viewId = id;
    this.viewTone = tone;
    this.viewArm?.setHeld(id, tone);
  }

  /** What is on the player's off arm: the shield from its slot, or nothing. */
  setShield(id: number, tone: Tone): void {
    this.viewShield = id;
    this.playerModel?.setShield(id, tone);
    this.viewArm?.setShield(id, tone);
  }

  /** Which hand the weapon is in - the body's and the one on the screen. */
  setHand(hand: Hand): void {
    this.hand = hand;
    this.playerModel?.setHand(hand);
    this.viewArm?.setHand(hand);
  }

  /** Where the view is easing to, kept apart from the camera's own number. */
  private fov = WALK_FOV;

  /**
   * Widen the view a little while running.
   *
   * Eased rather than switched: a view that jumps five degrees the instant a
   * key goes down is a flinch, not a stride. The projection matrix is only
   * rebuilt when the number actually moved, so standing still costs nothing.
   */
  easeFieldOfView(running: boolean, dt: number): void {
    const target = running ? RUN_FOV : WALK_FOV;
    if (Math.abs(this.fov - target) < 0.01) return;
    this.fov += (target - this.fov) * (1 - Math.exp(-FOV_EASE * dt));
    if (Math.abs(this.fov - target) < 0.01) this.fov = target;
    this.camera.fov = this.fov;
    this.camera.updateProjectionMatrix();
  }

  private onResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };

  /**
   * Queue meshing work for any chunk whose geometry is out of date.
   *
   * Cheap to call every frame: it compares a version number per chunk and
   * does nothing when nothing has changed. The actual meshing happens on
   * worker threads, so an edit never costs a frame here.
   */
  /** Blocks drawn by a model: the скрині. */
  private readonly blockModels: BlockModels;

  syncChunks(world: World): void {
    let submitted = 0;
    this.blockModels.flushWaiting((x, y, z) => world.getBlock(x, y, z), CHUNK_SIZE);
    this.blockModels.setTint(this.sky.worldTint);
    for (const chunk of world.allChunks()) {
      if (submitted >= MAX_SUBMITS_PER_FRAME) break;

      const key = `${chunk.cx},${chunk.cy},${chunk.cz}`;
      if (this.meshedVersion.get(key) === chunk.version) continue;
      if (this.meshPool.isBusy(key)) continue;

      // The chests in it, stood as models; see blockModels.
      this.blockModels.syncChunk(key, chunk.cx * CHUNK_SIZE, chunk.cy * CHUNK_SIZE,
        chunk.cz * CHUNK_SIZE, CHUNK_SIZE, (x, y, z) => world.getBlock(x, y, z));
      this.meshPool.submit({
        key,
        cx: chunk.cx, cy: chunk.cy, cz: chunk.cz,
        version: chunk.version,
        volume: world.extractPadded(chunk.cx, chunk.cy, chunk.cz),
        light: world.extractPaddedLight(chunk.cx, chunk.cy, chunk.cz),
      });
      submitted++;
    }
    this.stats = {
      chunks: this.chunkMeshes.size,
      triangles: this.triangleCount,
      meshing: this.meshPool.pending,
    };
  }

  /**
   * Let go of chunks the world has thrown away.
   *
   * A world grown as it is walked into forgets the country behind it, and a
   * mesh of ground that is no longer there would hang in the air until
   * something else happened to that key - which, for country nobody is going
   * back to, is never.
   */
  dropChunks(list: readonly { cx: number; cy: number; cz: number }[]): void {
    for (const at of list) {
      const key = `${at.cx},${at.cy},${at.cz}`;
      const mesh = this.chunkMeshes.get(key);
      if (mesh !== undefined) {
        this.triangleCount -= (mesh.geometry.getIndex()?.count ?? 0) / 3;
        mesh.geometry.dispose();
        this.scene.remove(mesh);
        this.chunkMeshes.delete(key);
      }
      this.blockModels.dropChunk(key);
      // And the version, or the chunk would never be meshed again if the
      // player walked back and the world grew it a second time.
      this.meshedVersion.delete(key);
    }
  }

  /**
   * Forget which version of each chunk is on screen, so every chunk is meshed
   * again. Needed after the world is regenerated under the renderer's feet:
   * the chunk objects are new, and a new chunk's version could coincide with
   * the version already drawn.
   */
  invalidateMeshes(): void {
    this.meshedVersion.clear();
    this.blockModels.clear();
  }

  /**
   * Install a finished mesh.
   *
   * A result can be stale - the chunk may have changed again while the worker
   * was busy. Install it anyway rather than dropping a frame of geometry; the
   * version bookkeeping means syncChunks will simply queue it again.
   */
  private applyMesh(result: MeshResponse): void {
    const { key } = result;

    const existing = this.chunkMeshes.get(key);
    if (existing !== undefined) {
      this.triangleCount -= (existing.geometry.getIndex()?.count ?? 0) / 3;
      existing.geometry.dispose();
      this.scene.remove(existing);
      this.chunkMeshes.delete(key);
    }
    this.meshedVersion.set(key, result.version);

    const opaqueCount = result.indices.length;
    const blendedCount = result.translucentIndices.length;
    if (opaqueCount + blendedCount === 0) return;

    // One vertex buffer, one index buffer, two groups. Three.js splits groups
    // into separate render items and draws every opaque one before any
    // transparent one, which is exactly the ordering water needs.
    const allIndices = new Uint32Array(opaqueCount + blendedCount);
    allIndices.set(result.indices, 0);
    allIndices.set(result.translucentIndices, opaqueCount);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(result.positions, 3));
    geometry.setAttribute("normal", new THREE.BufferAttribute(result.normals, 3));
    geometry.setAttribute("uv", new THREE.BufferAttribute(result.uvs, 2));
    geometry.setAttribute("layer", new THREE.BufferAttribute(result.layers, 1));
    geometry.setAttribute("shade", new THREE.BufferAttribute(result.shades, 1));
    geometry.setAttribute("light", new THREE.BufferAttribute(result.lights, 1));
    geometry.setIndex(new THREE.BufferAttribute(allIndices, 1));
    if (opaqueCount > 0) geometry.addGroup(0, opaqueCount, 0);
    if (blendedCount > 0) geometry.addGroup(opaqueCount, blendedCount, 1);
    geometry.computeBoundingSphere();

    const mesh = new THREE.Mesh(geometry, this.materials);
    mesh.position.set(
      result.cx * CHUNK_SIZE,
      result.cy * CHUNK_SIZE,
      result.cz * CHUNK_SIZE,
    );
    // Chunks never move; skip the per-frame matrix recompute.
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    this.scene.add(mesh);
    this.chunkMeshes.set(key, mesh);
    this.triangleCount += result.indices.length / 3;
  }

  /** How many worker threads are meshing chunks. */
  get workerCount(): number {
    return this.meshPool.size;
  }

  /** Swap the player's skin, rebuilding the model. */
  setSkin(def: SkinDef): void {
    if (this.playerModel !== null) {
      this.scene.remove(this.playerModel.group);
      this.playerModel.dispose();
    }
    this.playerModel = new PlayerModel(def);

    // Your own arm wears the same skin, so changing козак changes the hand
    // holding the axe as well as the body walking away from the camera.
    if (this.viewArm !== null) {
      this.viewHand.remove(this.viewArm.group);
      this.viewArm.dispose();
    }
    this.viewArm = new ViewArm(def);
    this.viewHand.add(this.viewArm.group);
    this.viewArm.setHeld(this.viewId, this.viewTone);
    this.viewArm.setShield(this.viewShield, this.viewTone);
    this.viewArm.setHand(this.hand);
    this.playerModel.setHeld(this.viewId, this.viewTone);
    this.playerModel.setShield(this.viewShield, this.viewTone);
    this.playerModel.setHand(this.hand);
    this.scene.add(this.playerModel.group);
  }

  /**
   * Bring the villager bodies in line with the list of villagers.
   *
   * Adds what is new, drops what has gone, and moves the rest. Called every
   * frame, so it has to be cheap when nothing has changed - and it is: the
   * common case is the position writes.
   */
  syncVillagers(list: readonly Villager[]): void {
    const live = new Set<number>();

    for (const v of list) {
      let model = this.villagerModels.get(v.id);
      if (!this.nearEnough(v.x, v.y, v.z, model !== undefined)) continue;
      live.add(v.id);
      if (model === undefined) {
        model = new PlayerModel(skinById(v.skinId));
        this.villagerModels.set(v.id, model);
        this.scene.add(model.group);
        model.setTint(this.sky.worldTint);
      }
      /*
       * Walking now, and lying down at night.
       *
       * The stride advances with distance walked rather than with time, the
       * same as the player's, so the feet do not slide. Asleep, the body is
       * laid on its back at bed height - a villager standing bolt upright
       * beside a bed all night was the first thing anybody noticed.
       */
      if (v.asleep) {
        model.group.position.set(v.x, v.y + 0.35, v.z);
        model.group.rotation.x = -Math.PI / 2;
        model.setPose(v.yaw, 0, 0, 0);
      } else {
        model.group.rotation.x = 0;
        model.group.position.set(v.x, v.y, v.z);
        const moving = Math.hypot(v.vx, v.vz);
        model.setPose(v.yaw, 0, v.walked * 2.1, moving > 0.4 ? 1 : 0);
      }
      // Set here, where the body and the thing that was hit are both in hand.
      // updateEnvironment only has the models, and cannot tell which of them
      // was just struck.
      model.setHurt(v.vitals.glow);
    }

    for (const [id, model] of this.villagerModels) {
      if (live.has(id)) continue;
      this.scene.remove(model.group);
      model.dispose();
      this.villagerModels.delete(id);
    }
  }

  /**
   * Bring the orc bodies in line with the orcs.
   *
   * The same shape as syncVillagers, and deliberately not merged with it: a
   * villager turns to face whoever hit him and an orc faces where it is going,
   * and the day one of them animates differently the merged version would have
   * to be pulled apart again.
   */
  syncOrcs(list: readonly Orc[], dt = 0): void {
    const live = new Set<number>();

    for (const orc of list) {
      let model = this.orcModels.get(orc.id);
      if (!this.nearEnough(orc.x, orc.y, orc.z, model !== undefined)) continue;
      live.add(orc.id);
      if (model === undefined) {
        // Each breed wears its own skin: which orc is coming for you and
        // which is standing off has to be readable across a courtyard.
        model = new PlayerModel(skinById(orc.breed.skinId));
        this.orcModels.set(orc.id, model);
        this.scene.add(model.group);
        model.setTint(this.sky.worldTint);
      }
      model.group.position.set(orc.x, orc.y, orc.z);
      // Stride from how fast it is actually moving, so one standing at a wall
      // marks time instead of sprinting on the spot.
      const speed = Math.hypot(orc.vx, orc.vz);
      // Measured against its own top speed, so the ватажок does not walk with
      // its legs pinned at full stride simply because it is faster.
      model.setHurt(orc.vitals.glow);
      /*
       * With its weapon in its fist.
       *
       * Asked for by name - "скелети з залізними мечами" - and it turned out
       * nothing that comes at you had ever been drawn holding anything: the
       * орк's шабля was a number in a table and never a shape on the screen.
       * `setHeld` costs nothing on the frames where it has not changed, which
       * is all of them after the first.
       */
      model.setHeld(orc.breed.wields ?? 0, this.viewTone);
      model.setShield(orc.breed.shields ?? 0, this.viewTone);
      /*
       * And swinging it when it strikes - "орки нехай рухають рукою зі
       * зброєю, коли б'ють". The same clock as another player's blow, for the
       * same reason: `struck` going up starts a swing, and the swing runs its
       * own length from there, so it reads the same whether the orc is
       * stepped here or mirrored from fifteen snapshots a second.
       */
      let left = Math.max(0, (this.orcSwing.get(orc.id) ?? 0) - dt);
      if (orc.struck !== (this.orcBlows.get(orc.id) ?? orc.struck)) left = STRIKE_SECONDS;
      this.orcBlows.set(orc.id, orc.struck);
      this.orcSwing.set(orc.id, left);
      model.setPose(orc.yaw, 0, this.swingPhase,
        Math.min(1, speed / orc.breed.speed), strikeProgress(left));
    }

    for (const [id, model] of this.orcModels) {
      if (live.has(id)) continue;
      this.scene.remove(model.group);
      model.dispose();
      this.orcModels.delete(id);
      this.orcBlows.delete(id);
      this.orcSwing.delete(id);
    }
  }

  /**
   * Bring the animal models in line with the animals.
   *
   * Its own map rather than sharing the orcs': a beast is a different model
   * class with a different pose, and the two were never going to merge.
   */
  /**
   * Змії, which are beasts to look at and nothing like them to step.
   *
   * Their own map and their own call rather than a flag inside `syncBeasts`:
   * a Змій has no herd, no grazing and no legs on the ground - he holds
   * station over his лігво - and the two lists have nothing in common except
   * that both end up as a `BeastModel`.
   */
  syncDragons(list: readonly Dragon[]): void {
    const live = new Set<number>();
    for (const d of list) {
      if (d.slain) continue;
      let model = this.dragonModels.get(d.id);
      if (!this.nearEnough(d.x, d.y, d.z, model !== undefined)) continue;
      live.add(d.id);
      if (model === undefined) {
        model = new BeastModel("zmii");
        this.dragonModels.set(d.id, model);
        this.scene.add(model.group);
        model.setTint(this.sky.worldTint);
      }
      model.group.position.set(d.x, d.y, d.z);
      // Wings beating and legs still: he is in the air the whole time, so the
      // stride that swings a тур's legs is given to the wings and nothing
      // else.
      model.setPose(d.yaw, this.swingPhase, 1);
    }
    for (const [id, model] of this.dragonModels) {
      if (live.has(id)) continue;
      this.scene.remove(model.group);
      this.dragonModels.delete(id);
    }
  }

  syncBeasts(list: readonly Beast[]): void {
    const live = new Set<number>();

    for (const beast of list) {
      let model = this.beastModels.get(beast.id);
      if (!this.nearEnough(beast.x, beast.y, beast.z, model !== undefined)) continue;
      live.add(beast.id);
      if (model === undefined) {
        model = new BeastModel(beast.breed.kind);
        this.beastModels.set(beast.id, model);
        this.scene.add(model.group);
        model.setTint(this.sky.worldTint);
      }
      model.group.position.set(beast.x, beast.y, beast.z);
      const speed = Math.hypot(beast.vx, beast.vz);
      model.setHurt(beast.vitals.glow);
      model.setPose(beast.yaw, this.swingPhase, Math.min(1, speed / beast.breed.walk));
    }

    for (const [id, model] of this.beastModels) {
      if (live.has(id)) continue;
      this.scene.remove(model.group);
      model.dispose();
      this.beastModels.delete(id);
    }
  }

  /** Bring the карась models in line with the fish. */
  /**
   * The boats, which are the one thing here that is furniture rather than a
   * body: it sits where it was left until somebody gets into it.
   */
  syncBoats(list: readonly Boat[]): void {
    const live = new Set<number>();
    for (const boat of list) {
      let model = this.boatModels.get(boat.id);
      if (!this.nearEnough(boat.x, boat.y, boat.z, model !== undefined)) continue;
      live.add(boat.id);
      if (model === undefined) {
        model = new BoatModel();
        this.boatModels.set(boat.id, model);
        this.scene.add(model.group);
      }
      model.setPose(boat.x, boat.y, boat.z, boat.yaw);
    }
    for (const [id, model] of this.boatModels) {
      if (live.has(id)) continue;
      this.scene.remove(model.group);
      model.dispose();
      this.boatModels.delete(id);
    }
  }

  /**
   * The танки, which are furniture with a driver's seat in them.
   *
   * The same arrangement the boats have, for the same reason: a machine
   * sits where it was left until somebody gets in, and what is drawn is
   * whatever the list says - ours and everybody else's alike.
   */
  syncTanks(list: readonly Tank[]): void {
    const live = new Set<number>();
    for (const tank of list) {
      let model = this.tankModels.get(tank.id);
      if (!this.nearEnough(tank.x, tank.y, tank.z, model !== undefined)) continue;
      live.add(tank.id);
      if (model === undefined) {
        model = new TankModel();
        this.tankModels.set(tank.id, model);
        this.scene.add(model.group);
      }
      model.setPose(tank);
    }
    for (const [id, model] of this.tankModels) {
      if (live.has(id)) continue;
      this.scene.remove(model.group);
      model.dispose();
      this.tankModels.delete(id);
    }
  }

  /** Where the other players' models actually are. For probes and checks. */
  drawnPlayers(): { id: number; x: number; y: number; z: number }[] {
    return [...this.otherModels.entries()].map(([id, m]) => ({
      id, x: Math.round(m.group.position.x), y: Math.round(m.group.position.y),
      z: Math.round(m.group.position.z),
    }));
  }

  syncFish(list: readonly Fish[]): void {
    const live = new Set<number>();

    for (const fish of list) {
      let model = this.fishModels.get(fish.id);
      if (!this.nearEnough(fish.x, fish.y, fish.z, model !== undefined)) continue;
      live.add(fish.id);
      if (model === undefined) {
        model = new FishModel();
        this.fishModels.set(fish.id, model);
        this.scene.add(model.group);
      }
      model.group.position.set(fish.x, fish.y, fish.z);
      const speed = Math.hypot(fish.vx, fish.vy, fish.vz);
      model.setPose(fish.yaw, fish.pitch, this.swingPhase, Math.min(1, speed / 1.1));
    }

    for (const [id, model] of this.fishModels) {
      if (live.has(id)) continue;
      this.scene.remove(model.group);
      model.dispose();
      this.fishModels.delete(id);
    }
  }

  /**
   * Bring the arrow meshes in line with the arrows.
   *
   * The shaft is drawn along -Z so that the same yaw and pitch the physics
   * uses point it: an arrow and a player face the same way for the same
   * numbers, and there is no second convention to get backwards.
   */
  /**
   * The other people in the world.
   *
   * Drawn the way the villagers are - a body wearing their chosen skin, at
   * the position the server last reported. Not interpolated yet, so at
   * fifteen snapshots a second they step rather than walk; smoothing between
   * snapshots is the next thing this wants, and it is written down in
   * NETWORK.md rather than half-done here.
   */
  syncPlayers(list: readonly {
    id: number; x: number; y: number; z: number;
    yaw: number; pitch: number; skinId: string;
    drone?: { x: number; y: number; z: number; yaw: number };
    held?: number; blows?: number;
    /** Blocks a second, from the snapshots. See core/net/smooth. */
    speed?: number;
  }[], dt = 0): void {
    const live = new Set<number>();
    const flying = new Set<number>();
    for (const other of list) {
      /*
       * Their drone, if they have one up.
       *
       * Drawn here rather than in a list of its own because it belongs to
       * exactly one of them: a separate list would have to be joined back up
       * to the people at the other end for no gain. You never see your own -
       * the camera is inside it - so this is the only place one is ever
       * drawn.
       */
      if (other.drone !== undefined &&
          this.nearEnough(other.drone.x, other.drone.y, other.drone.z, true)) {
        flying.add(other.id);
        let seen = this.otherDrones.get(other.id);
        if (seen === undefined) {
          const mesh = makeHeldMesh(PlainItemId.Drone);
          if (mesh !== null) {
            mesh.scale.setScalar(0.7);
            this.scene.add(mesh);
            this.otherDrones.set(other.id, mesh);
            seen = mesh;
          }
        }
        if (seen !== undefined) {
          seen.visible = true;
          seen.position.set(other.drone.x, other.drone.y, other.drone.z);
          // The drawing is a plan view, so it is laid flat rather than stood
          // up the way a sabre is.
          seen.rotation.set(-Math.PI / 2, 0, -other.drone.yaw);
        }
      }
      /*
       * Too far to draw is too far to *keep*, and that is the whole of the
       * bug this line fixes.
       *
       * Every other list here counts a body as live only once it is near
       * enough to be drawn. This one counted it live at the top of the loop,
       * because the drone above belongs to the same person - so somebody who
       * walked out of sight was never removed and never moved either: their
       * model stood where it was last drawn, for ever, with their name over
       * it. It took a map a hundred and ninety-two blocks across to show:
       * two armies started ninety-seven blocks apart and each of them spent
       * the match looking at a statue of the other standing in their own
       * camp.
       */
      if (!this.nearEnough(other.x, other.y, other.z,
        this.otherModels.has(other.id))) continue;
      live.add(other.id);
      let model = this.otherModels.get(other.id);
      if (model === undefined) {
        model = new PlayerModel(skinById(other.skinId));
        this.otherModels.set(other.id, model);
        this.scene.add(model.group);
        model.setTint(this.sky.worldTint);
      }
      /*
       * How fast they are going, from the snapshots rather than the frame.
       *
       * This used to be the distance between two calls, which was one
       * snapshot's worth because a snapshot was all that ever changed. Now
       * the positions are interpolated and two calls are a sixtieth of a
       * second apart, so the same sum comes out four times too small and
       * everybody tiptoes. The sender works it out and passes it.
       */
      const speed = other.speed ?? (() => {
        const last = this.otherWhere.get(other.id);
        return last === undefined || dt <= 0
          ? 0 : Math.hypot(other.x - last.x, other.z - last.z) / dt;
      })();
      this.otherWhere.set(other.id, { x: other.x, z: other.z });
      model.group.position.set(other.x, other.y, other.z);

      /*
       * What they are holding, drawn in this viewer's own tone.
       *
       * The сабля has two pictures and which one is drawn describes whoever
       * is looking at it, not whoever is carrying it - the same rule the
       * lines of dialogue follow. So the id travels and the picture does not.
       */
      model.setHeld(other.held ?? 0, this.viewTone);

      /*
       * And the blow, run on this machine's clock.
       *
       * What arrives is a count of blows struck, not a timer: a blow is 0.28
       * of a second and snapshots come fifteen a second, so a "swinging now"
       * flag would stick at whatever the last snapshot said for four frames
       * at a time and read as a stutter. A number that goes up says a blow
       * *started*, and the arm swings here from there - which is why a
       * dropped snapshot loses nothing and a repeated one draws nothing
       * twice.
       */
      const struck = other.blows ?? 0;
      let left = Math.max(0, (this.otherSwing.get(other.id) ?? 0) - dt);
      if (struck !== (this.otherBlows.get(other.id) ?? struck)) left = STRIKE_SECONDS;
      this.otherBlows.set(other.id, struck);
      this.otherSwing.set(other.id, left);

      /*
       * The stride, and the phase that drives it.
       *
       * Advanced by the distance they actually covered, the same way the
       * player's own is, so the feet keep up with the body instead of
       * sliding. Measured against a walking pace: standing still settles the
       * limbs rather than marking time.
       */
      this.otherPhase.set(other.id,
        (this.otherPhase.get(other.id) ?? 0) + speed * dt * STRIDE);
      const amount = Math.min(1, speed / 4.3);
      model.setPose(other.yaw, other.pitch,
        amount < 0.02 ? 0 : this.otherPhase.get(other.id) ?? 0,
        amount, strikeProgress(left));
    }
    // A drone whose owner has landed it, or left, stops being drawn.
    for (const [id, mesh] of this.otherDrones) {
      if (flying.has(id)) continue;
      this.scene.remove(mesh);
      disposeHeldMesh(mesh);
      this.otherDrones.delete(id);
    }
    for (const [id, model] of this.otherModels) {
      if (live.has(id)) continue;
      this.scene.remove(model.group);
      this.otherModels.delete(id);
      this.otherWhere.delete(id);
    }
  }

  syncArrows(list: readonly Arrow[]): void {
    if (this.arrowGeometry === null) {
      // Long and thin, its origin at the middle, lying along -Z.
      this.arrowGeometry = new THREE.BoxGeometry(0.06, 0.06, 0.7);
      this.arrowMaterial = new THREE.MeshBasicMaterial({ color: 0xb08a52 });
    }

    const live = new Set<number>();
    for (const arrow of list) {
      live.add(arrow.id);
      let mesh = this.arrowMeshes.get(arrow.id);
      if (mesh === undefined) {
        mesh = new THREE.Mesh(this.arrowGeometry, this.arrowMaterial!);
        this.arrowMeshes.set(arrow.id, mesh);
        this.scene.add(mesh);
      }
      mesh.position.set(arrow.x, arrow.y, arrow.z);
      const { yaw, pitch } = arrowAim(arrow);
      mesh.rotation.set(0, 0, 0);
      mesh.rotateY(yaw);
      mesh.rotateX(pitch);
    }

    for (const [id, mesh] of this.arrowMeshes) {
      if (live.has(id)) continue;
      this.scene.remove(mesh);
      this.arrowMeshes.delete(id);
    }
  }

  /**
   * The bolts in the air.
   *
   * A small bright cube. Not tinted by the hour like the rest of the world -
   * a thing that is on fire is its own light source, and one that dimmed at
   * dusk would look like a stone rather than a flame.
   */
  syncFirebolts(list: readonly Firebolt[]): void {
    if (this.boltGeometry === null) {
      this.boltGeometry = new THREE.BoxGeometry(0.34, 0.34, 0.34);
      this.boltMaterial = new THREE.MeshBasicMaterial({ color: 0xff9a2e });
      /*
       * Чахлик's fire, which is blue - "вогонь нехай буде синій".
       *
       * A second material rather than a tint on the first: they are both
       * shared by every bolt in the air, so setting the colour per bolt would
       * repaint every other bolt along with it.
       */
      this.coldMaterial = new THREE.MeshBasicMaterial({ color: 0x54c8ff });
    }
    const live = new Set<number>();
    for (const bolt of list) {
      live.add(bolt.id);
      /*
       * A Змій's breath is a ball of live flame, not a tumbling stone.
       *
       * "Make the fireball custom, bigger, like live flame." The same two
       * crossed flame quads everything burning wears, the size of his head
       * and beating, round a glowing core so it reads as a ball and not as
       * a bonfire flying sideways.
       */
      if (bolt.breath) {
        let flame = this.breathFlames.get(bolt.id);
        if (flame === undefined) {
          flame = this.makeFlame();
          const core = new THREE.Mesh(
            new THREE.SphereGeometry(0.55, 10, 8),
            new THREE.MeshBasicMaterial({ color: 0xffb040, toneMapped: false }));
          core.name = "core";
          flame.add(core);
          this.breathFlames.set(bolt.id, flame);
          this.scene.add(flame);
        }
        this.posFlame(flame, bolt.x, bolt.y - 1.1, bolt.z, 1.9, 2.3, bolt.id);
        const core = flame.getObjectByName("core");
        if (core !== undefined) core.position.set(0, -(2.3 * 0.9) / 2 + 1.1, 0);
        continue;
      }
      let mesh = this.boltMeshes.get(bolt.id);
      if (mesh === undefined) {
        mesh = new THREE.Mesh(this.boltGeometry,
          (bolt.cold ? this.coldMaterial : this.boltMaterial)!);
        this.boltMeshes.set(bolt.id, mesh);
        this.scene.add(mesh);
      }
      mesh.position.set(bolt.x, bolt.y, bolt.z);
      // Tumbling, because a lobbed stone does.
      mesh.rotation.x = bolt.age * 7;
      mesh.rotation.z = bolt.age * 5;
    }
    for (const [id, mesh] of this.boltMeshes) {
      if (live.has(id)) continue;
      this.scene.remove(mesh);
      this.boltMeshes.delete(id);
    }
    for (const [id, flame] of this.breathFlames) {
      if (live.has(id)) continue;
      this.scene.remove(flame);
      this.breathFlames.delete(id);
    }
  }

  /**
   * The flame texture, painted in core and uploaded once.
   *
   * Two of them, from two seeds: a pair of crossed billboards cut from one
   * picture reads as a cross, and from two it reads as fire.
   */
  /** The Змій's breaths in the air, each a beating flame round a core. */
  private readonly breathFlames = new Map<number, THREE.Group>();

  private flameMaps: THREE.Texture[] | null = null;

  private flameTextures(): THREE.Texture[] {
    if (this.flameMaps !== null) return this.flameMaps;
    this.flameMaps = [1, 2].map((seed) => {
      /*
       * Copied into a plain byte array on the way in.
       *
       * `paintFlame` hands back a `Uint8ClampedArray`, which is the right
       * thing for a painter and is not what a texture takes: three.js wants
       * a view on an ordinary ArrayBuffer. One copy, once, on the first
       * fire anybody lights.
       */
      const map = new THREE.DataTexture(
        new Uint8Array(paintFlame(seed)), FLAME_W, FLAME_H, THREE.RGBAFormat);
      // Nearest, like everything else here: this is a game of visible pixels
      // and a smoothed flame would be the one soft thing on the screen.
      map.magFilter = THREE.NearestFilter;
      map.minFilter = THREE.NearestFilter;
      map.needsUpdate = true;
      return map;
    });
    return this.flameMaps;
  }

  /**
   * A flame: two quads crossed, standing on whatever is burning.
   *
   * "Зараз просто блоки мерехтять і змінюють колір, над блоком має бути
   * полум'я." What was here was a translucent orange cube wrapped round the
   * block, which is exactly that: the block went orange and pulsed.
   */
  private makeFlame(): THREE.Group {
    const group = new THREE.Group();
    for (const [i, map] of this.flameTextures().entries()) {
      const quad = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({
          map, transparent: true, alphaTest: 0.35, depthWrite: false,
          side: THREE.DoubleSide, toneMapped: false,
        }));
      // Crossed, and neither of them square on to the world: a flame seen
      // exactly edge-on would otherwise vanish.
      quad.rotation.y = Math.PI / 4 + (i * Math.PI) / 2;
      group.add(quad);
    }
    return group;
  }

  /**
   * Make one flame flicker, and stand it where it belongs.
   *
   * The two quads are scaled rather than swapped: a flame that changes
   * picture reads as a flicker-book, and one that breathes reads as fire.
   * Every cell gets its own phase off its own position, so a burning wall is
   * not one flame drawn twenty times in step.
   */
  private posFlame(
    flame: THREE.Group, x: number, y: number, z: number,
    wide: number, tall: number, phase: number,
  ): void {
    const t = performance.now() / 1000;
    const beat = 0.86 + 0.14 * Math.sin(t * 7 + phase) +
      0.06 * Math.sin(t * 17 + phase * 3);
    flame.position.set(x, y + (tall * beat) / 2, z);
    for (const quad of flame.children) {
      quad.scale.set(wide, tall * beat, 1);
    }
  }

  /**
   * What is alight.
   *
   * A flame standing on the burning block, keyed by cell: a fire has no
   * identity, it is a property of a place, and the place is what comes and
   * goes.
   */
  syncFire(cells: readonly { x: number; y: number; z: number }[]): void {
    const live = new Set<string>();
    for (const cell of cells) {
      const at = `${cell.x},${cell.y},${cell.z}`;
      live.add(at);
      let flame = this.flameMeshes.get(at);
      if (flame === undefined) {
        flame = this.makeFlame();
        this.flameMeshes.set(at, flame);
        this.scene.add(flame);
      }
      /*
       * On top of the block rather than round it.
       *
       * A block that is burning is a block with a fire on it: the flame
       * stands on its top face, a little wider than the cell so it is not a
       * neat square of fire, and every cell gutters to its own clock.
       */
      this.posFlame(flame, cell.x + 0.5, cell.y + 1, cell.z + 0.5,
        1.1, 1.3, cell.x * 1.7 + cell.z * 2.3 + cell.y);
    }
    for (const [at, flame] of this.flameMeshes) {
      if (live.has(at)) continue;
      this.scene.remove(flame);
      for (const quad of flame.children) {
        (quad as THREE.Mesh).geometry.dispose();
      }
      this.flameMeshes.delete(at);
    }
  }

  /**
   * Your own drone, while you are not looking through it.
   *
   * Never drawn before, and it did not need to be: the camera was inside it
   * whenever it existed. Now it can be up with nobody at the controls - you
   * stepped out and it is holding its station on what is left of the battery
   * - and a machine hanging over the field that nothing draws is a machine
   * you cannot find again. Pass null while you are in it: the first attempt
   * at drawing one filled the screen with the inside of its own shell.
   */
  syncOwnDrone(at: { x: number; y: number; z: number; yaw: number } | null): void {
    if (at === null) {
      if (this.ownDrone !== null) this.ownDrone.visible = false;
      return;
    }
    if (this.ownDrone === null) {
      const mesh = makeHeldMesh(PlainItemId.Drone);
      if (mesh === null) return;
      mesh.scale.setScalar(0.7);
      this.scene.add(mesh);
      this.ownDrone = mesh;
    }
    this.ownDrone.visible = true;
    this.ownDrone.position.set(at.x, at.y, at.z);
    // Laid flat rather than stood up: the drawing is a plan view.
    this.ownDrone.rotation.set(-Math.PI / 2, 0, -at.yaw);
  }

  /**
   * Flames on a body.
   *
   * Kept apart from `syncFire` because a cell fire never moves and a burning
   * свиня does: those meshes are keyed by position and placed once, and a
   * body needs its mesh followed every frame. The material and the guttering
   * are the same, so a man alight and the roof he is standing under flicker
   * together.
   */
  syncBodyFire(
    bodies: readonly {
      key: string; x: number; y: number; z: number;
      width: number; height: number;
    }[],
  ): void {
    const live = new Set<string>();
    for (const body of bodies) {
      live.add(body.key);
      let flame = this.bodyFlames.get(body.key);
      if (flame === undefined) {
        flame = this.makeFlame();
        this.bodyFlames.set(body.key, flame);
        this.scene.add(flame);
      }
      /*
       * Round the body rather than on top of it.
       *
       * A свиня alight is on fire all over, not wearing a candle - so the
       * flame is as tall as the animal and a little wider, standing up
       * through it from the feet.
       */
      this.posFlame(flame, body.x, body.y, body.z,
        body.width + 0.35, body.height + 0.35, body.x * 3 + body.z);
    }
    for (const [gone, flame] of this.bodyFlames) {
      if (live.has(gone)) continue;
      this.scene.remove(flame);
      for (const quad of flame.children) {
        (quad as THREE.Mesh).geometry.dispose();
      }
      this.bodyFlames.delete(gone);
    }
  }


  /**
   * Bring the meshes in line with what is lying on the ground.
   *
   * They turn and bob, which is not decoration: a small cube resting on a
   * floor of the same texture is invisible until it moves.
   */
  syncPickups(list: readonly Pickup[]): void {
    const live = new Set<number>();
    const now = performance.now() / 1000;

    for (const item of list) {
      live.add(item.id);
      const tile = itemTile(item.stack.id);
      let mesh = this.pickupMeshes.get(item.id);
      if (mesh === undefined || mesh.userData.tile !== tile) {
        if (mesh !== undefined) this.scene.remove(mesh);
        // The opaque one of the pair: a dropped thing is never see-through,
        // even when the block it came from was. Its shape - a cube for a
        // block, its picture for anything drawn flat - see render/pickupShape.
        // A modelled thing lies as its model, the size a picture lies at.
        const modelled = heldModel(item.stack.id);
        if (modelled !== null) {
          modelled.scale.setScalar(PICKUP_PICTURE);
          mesh = modelled;
        } else {
          mesh = new THREE.Mesh(pickupGeometry(item.stack.id), this.materials[0]);
        }
        mesh.userData.tile = tile;
        this.pickupMeshes.set(item.id, mesh);
        this.scene.add(mesh);
      }
      // The bob is keyed to the entity id so a heap does not pulse in unison.
      const bob = Math.sin(now * 2.2 + item.id) * 0.06;
      mesh.position.set(item.x, item.y + bob, item.z);
      mesh.rotation.y = now * 1.1 + item.id;
    }

    for (const [id, mesh] of this.pickupMeshes) {
      if (live.has(id)) continue;
      this.scene.remove(mesh);
      this.pickupMeshes.delete(id);
    }
  }

  /** Where on screen a point in the world lands, for speech bubbles. */
  project(x: number, y: number, z: number): { x: number; y: number; visible: boolean } {
    this.projected.set(x, y, z).project(this.camera);
    const canvas = this.renderer.domElement;
    return {
      x: (this.projected.x * 0.5 + 0.5) * canvas.clientWidth,
      y: (-this.projected.y * 0.5 + 0.5) * canvas.clientHeight,
      // z leaves the near plane behind the camera as > 1 once projected.
      visible: this.projected.z < 1,
    };
  }

  /**
   * Point the camera using the player's own position and angles.
   *
   * In third person the camera pulls back along the view axis, and a raycast
   * stops it from ending up inside terrain when you back into a wall.
   */
  /**
   * Put the camera behind whatever is being flown or walked.
   *
   * Takes an eye and two angles rather than a `PlayerController`, so a drone
   * can be looked through without pretending to be a player. It was the
   * player's own type for a year and there was no second thing to look
   * through; now there is.
   */
  syncCamera(player: CameraTarget, isSolid: SolidTest, dt = 0): void {
    const raw = player.eyePosition();
    /*
     * The camera lags behind a step and catches up.
     *
     * "При переході гравця на блок вище він ніби телепортується на блок
     * вище." Walking onto a ledge lifts the body the whole block inside one
     * frame - it has to, or nothing else would know where it is - and a
     * camera bolted to the eye goes with it. What is smoothed is the
     * picture, never the body: `easeStep` says how far behind to stay.
     */
    if (Number.isFinite(this.lastEyeY)) {
      this.stepLag = easeStep(
        this.stepLag, raw.y - this.lastEyeY, player.onGround === true, dt);
    }
    this.lastEyeY = raw.y;
    const eye = { x: raw.x, y: raw.y - this.stepLag, z: raw.z };

    const cosPitch = Math.cos(player.pitch);
    this.forward.set(
      -Math.sin(player.yaw) * cosPitch,
      Math.sin(player.pitch),
      -Math.cos(player.yaw) * cosPitch,
    );

    // Which way the camera pulls away from the eye: behind for the normal
    // third-person view, ahead of the player for the face-on one.
    const sign = this.cameraMode === "thirdFront" ? 1 : -1;

    if (this.cameraMode === "first") {
      this.camera.position.set(eye.x, eye.y, eye.z);
      this.camera.lookAt(
        eye.x + this.forward.x,
        eye.y + this.forward.y,
        eye.z + this.forward.z,
      );
      return;
    }

    const ox = this.forward.x * sign;
    const oy = this.forward.y * sign;
    const oz = this.forward.z * sign;

    // Stop short of any wall the camera would otherwise end up inside.
    let distance = THIRD_PERSON_DISTANCE;
    const hit = raycastVoxels(eye.x, eye.y, eye.z, ox, oy, oz, distance + 0.4, isSolid);
    if (hit !== null) {
      distance = Math.max(MIN_CAMERA_DISTANCE, hit.distance - 0.35);
    }

    this.camera.position.set(
      eye.x + ox * distance,
      eye.y + oy * distance,
      eye.z + oz * distance,
    );
    // Always look back toward the player, whichever side the camera is on.
    this.camera.lookAt(eye.x, eye.y, eye.z);
  }

  /**
   * Place and pose the player's body.
   *
   * Hidden in first person - you would be looking at the inside of your own
   * head.
   */
  syncPlayerModel(
    player: PlayerController, dt: number, strike = 0, hurt = 0,
  ): void {
    const model = this.playerModel;
    if (model === null) return;

    // The fist is drawn in first person, where the body is not, and hidden
    // behind you in the other views, where the body's own hand has the thing.
    // No hands while your eyes are elsewhere: the drone is in the air, not in
    // your fist.
    this.viewHand.visible = this.cameraMode === "first" && !this.showOwnBody;
    this.viewArm?.setStrike(strike);

    model.group.visible = this.cameraMode !== "first" || this.showOwnBody;
    if (!model.group.visible) return;

    model.group.position.set(player.position.x, player.position.y, player.position.z);

    const speed = Math.hypot(player.velocity.x, player.velocity.z);
    this.swingPhase += speed * dt * STRIDE;
    // Settle the limbs back to neutral when standing still.
    const amount = Math.min(1, speed / 4.3);
    if (amount < 0.02) this.swingPhase = 0;

    model.setHurt(hurt);
    model.setCrouch(player.crouching === true);
    model.setPose(player.yaw, player.pitch, this.swingPhase, amount, strike);
  }

  /**
   * Apply the time of day: move the sun and moon, repaint the sky, and tint
   * the terrain and fog to match. Call after syncCamera so the sky dome is
   * centred on where the camera actually ended up this frame.
   */
  updateEnvironment(cycle: DayNightCycle): void {
    this.sky.update(cycle, this.camera.position);

    // Wall-clock seconds, not the game clock: water runs at the same speed
    // whether the day is on its normal setting or racing at a hundred times.
    this.uniforms.uTime.value = performance.now() / 1000;

    // Light level and fog are shader uniforms - one multiply in the fragment
    // shader, no lighting pass.
    // Both materials share these uniform objects, so water is always lit the
    // same way as the ground under it.
    this.uniforms.uTint.value.copy(this.sky.worldTint);
    // How much the sun is giving, which scales skylight and nothing else.
    this.uniforms.uDaylight.value = cycle.daylight;
    this.uniforms.uFogColor.value.copy(this.sky.horizonColor);

    // The body darkens with the world, same as the terrain - and so does the
    // arm in front of the camera, or your own hand stays bright at midnight.
    this.playerModel?.setTint(this.sky.worldTint);
    this.viewArm?.setTint(this.sky.worldTint);
    for (const model of this.villagerModels.values()) {
      model.setTint(this.sky.worldTint);
    }
    for (const model of this.orcModels.values()) {
      model.setTint(this.sky.worldTint);
    }
    for (const model of this.beastModels.values()) {
      model.setTint(this.sky.worldTint);
    }
    // One call, not one per fish: they all share three materials.
    tintKaras(this.sky.worldTint);
    tintTanks(this.sky.worldTint);
    tintBoats(this.sky.worldTint);

    // scene.fog still drives the player model, which uses a stock material.
    (this.scene.fog as THREE.Fog).color.copy(this.sky.horizonColor);
    this.renderer.setClearColor(this.sky.horizonColor);
  }

  /**
   * Outline the block being aimed at, or pass null to hide the outline.
   *
   * `progress` is how far through breaking it the player is, 0 to 1, and
   * anything above nought cracks the block as well as outlining it.
   */
  setHighlight(
    block: { x: number; y: number; z: number } | null,
    progress = 0,
  ): void {
    if (block === null) {
      this.highlight.visible = false;
      this.cracking.visible = false;
      return;
    }
    this.highlight.visible = true;
    this.highlight.position.set(block.x + 0.5, block.y + 0.5, block.z + 0.5);

    if (progress <= 0) {
      this.cracking.visible = false;
      return;
    }
    // Ceil, so the first blow shows a crack rather than nothing: a hit that
    // changes nothing on the screen is a hit the player thinks was missed.
    const stage = Math.min(
      CRACK_STAGES, Math.max(1, Math.ceil(progress * CRACK_STAGES)));
    const material = this.cracking.material as THREE.MeshBasicMaterial;
    const map = this.crackTextures()[stage - 1]!;
    if (material.map !== map) {
      material.map = map;
      material.needsUpdate = true;
    }
    this.cracking.visible = true;
    this.cracking.position.set(block.x + 0.5, block.y + 0.5, block.z + 0.5);
  }

  /** The four stages of cracking, painted on the first block anybody digs. */
  private crackTextures(): THREE.Texture[] {
    if (this.crackMaps !== null) return this.crackMaps;
    this.crackMaps = Array.from({ length: CRACK_STAGES }, (_, i) => {
      const map = new THREE.DataTexture(
        new Uint8Array(paintCracks(i + 1)), CRACK_SIZE, CRACK_SIZE,
        THREE.RGBAFormat);
      map.magFilter = THREE.NearestFilter;
      map.minFilter = THREE.NearestFilter;
      map.needsUpdate = true;
      return map;
    });
    return this.crackMaps;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Close in the fog and tint everything blue while the camera is submerged.
   *
   * Cheap, and it is what makes water read as a volume you are inside rather
   * than a blue pane you happen to be behind.
   */
  applyUnderwater(submerged: boolean): void {
    if (submerged) {
      this.uniforms.uFogColor.value.setHex(UNDERWATER_FOG);
      this.uniforms.uFogNear.value = UNDERWATER_NEAR;
      this.uniforms.uFogFar.value = UNDERWATER_FAR;
      this.uniforms.uTint.value.multiply(UNDERWATER_TINT);
      this.renderer.setClearColor(UNDERWATER_FOG);
      (this.scene.fog as THREE.Fog).color.setHex(UNDERWATER_FOG);
      (this.scene.fog as THREE.Fog).near = UNDERWATER_NEAR;
      (this.scene.fog as THREE.Fog).far = UNDERWATER_FAR;
    } else {
      this.uniforms.uFogNear.value = FOG_NEAR;
      this.uniforms.uFogFar.value = FOG_FAR;
      (this.scene.fog as THREE.Fog).near = FOG_NEAR;
      (this.scene.fog as THREE.Fog).far = FOG_FAR;
    }
  }

  dispose(): void {
    this.meshPool.dispose();
  }
}
